/**
 * @fileoverview Vendor Modal Component
 *
 * Multi-mode modal for creating and editing vendor records.
 * Supports Quick Add, Full Add, and Edit modes.
 *
 * Quick Add: Vendor Name, Contact Name, Contact Phone, Contact Email,
 *   Vendor Category (multi-select), Is Client-Facing, Has Showroom,
 *   Address (conditional — shows when Has Showroom is checked)
 *
 * Full Add: + Vendor Website, Vendor Type (multi-select),
 *   Pool Work Vendor, Notes, Logo (Cloudinary upload)
 *
 * Edit: Same as Full Add, pre-populated with existing data.
 *
 * @requires react
 * @requires react-icons/fa
 * @requires ../../services/cloudinaryService
 * @requires ./AdminFormModal.css
 * @version 1.0.0
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  FaHardHat,
  FaTimes,
  FaCheck,
  FaSpinner,
  FaExclamationTriangle,
  FaTrash,
  FaChevronDown,
  FaCheckCircle,
  FaPlusCircle,
  FaCloudUploadAlt,
  FaSyncAlt,
} from "react-icons/fa";
import { uploadFile } from "../../services/cloudinaryService";
import { formatPhoneDisplay, formatPhoneInput } from "../../utils/phoneUtils";
import "./AdminFormModal.css";

// =============================================================================
// CONSTANTS
// =============================================================================

/** Available vendor categories matching Airtable Multiple Select options */
const VENDOR_CATEGORIES = [
  "Appliance",
  "Brick",
  "Cabinet",
  "Concrete",
  "Countertop",
  "Drywall",
  "Electrical",
  "Excavation & Grading",
  "Fencing",
  "Finish Carpentry",
  "Fireplace",
  "Flooring",
  "Framing",
  "Garage Door",
  "General Contractor / Internal",
  "Glass/Mirror",
  "Gutters",
  "HVAC",
  "Insulation",
  "Landscape",
  "Lighting",
  "Lumber",
  "Mantle",
  "Millwork",
  "Painting",
  "Photography/Videography",
  "Plumbing",
  "Pool Equipment",
  "Roofing",
  "Security/Low Voltage",
  "Siding",
  "Stone",
  "Tile / Masonry",
  "Window & Door",
];

/** Available vendor types matching Airtable Multiple Select options */
const VENDOR_TYPES = ["Supplier", "Installer"];

/** Default empty form state */
const EMPTY_FORM = {
  vendorName: "",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
  vendorCategory: [],
  isClientFacing: false,
  hasShowroom: false,
  address: "",
  website: "",
  vendorType: [],
  poolWorkVendor: false,
  notes: "",
  status: "",
  logo: null,
};

// =============================================================================
// MULTI-SELECT DROPDOWN SUB-COMPONENT
// =============================================================================

/**
 * Reusable multi-select dropdown with tags
 * @param {Object} props - Component props
 * @param {Array} props.options - Available options
 * @param {Array} props.selected - Currently selected values
 * @param {Function} props.onChange - Change handler
 * @param {string} props.placeholder - Placeholder text
 */
const MultiSelectDropdown = ({ options, selected, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  /** Close dropdown on outside click */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /**
   * Toggle an option in the selected array
   * @param {string} option - Option value to toggle
   */
  const toggleOption = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter((s) => s !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  /**
   * Remove a tag from selected
   * @param {Event} e - Click event
   * @param {string} option - Option to remove
   */
  const removeTag = (e, option) => {
    e.stopPropagation();
    onChange(selected.filter((s) => s !== option));
  };

  return (
    <div className="afm-multiselect-wrapper" ref={wrapperRef}>
      <div
        className={`afm-multiselect-trigger ${isOpen ? "afm-multiselect-open" : ""}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {selected.length > 0 ? (
          selected.map((item) => (
            <span key={item} className="afm-multiselect-tag">
              {item}
              <span
                className="afm-multiselect-tag-remove"
                onClick={(e) => removeTag(e, item)}
              >
                <FaTimes />
              </span>
            </span>
          ))
        ) : (
          <span className="afm-multiselect-placeholder">{placeholder}</span>
        )}
        <FaChevronDown className="afm-multiselect-chevron" />
      </div>

      {isOpen && (
        <div className="afm-multiselect-dropdown">
          {options.map((option) => (
            <div
              key={option}
              className={`afm-multiselect-option ${
                selected.includes(option) ? "afm-multiselect-selected" : ""
              }`}
              onClick={() => toggleOption(option)}
            >
              <input
                type="checkbox"
                checked={selected.includes(option)}
                onChange={() => {}}
                tabIndex={-1}
              />
              <span>{option}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// =============================================================================
// VENDOR MODAL COMPONENT
// =============================================================================

/**
 * VendorModal Component
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Close handler
 * @param {"quick"|"full"|"edit"} [props.mode="quick"] - Initial mode
 * @param {string} [props.recordId] - Vendor record ID (edit mode)
 * @param {Object} [props.vendorData] - Pre-loaded vendor data (edit mode)
 * @param {Function} [props.onSave] - Callback after successful save
 * @param {Object} props.mutations - { createVendor, updateVendor } mutation objects
 */
const VendorModal = ({
  isOpen,
  onClose,
  mode: initialMode = "quick",
  recordId = null,
  vendorData = null,
  onSave,
  mutations,
}) => {
  // ===================== STATE =====================

  const [currentMode, setCurrentMode] = useState(initialMode);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [isLogoDragging, setIsLogoDragging] = useState(false);
  const logoInputRef = useRef(null);

  /** Baseline form snapshot for detecting unsaved changes */
  const baselineRef = useRef({ ...EMPTY_FORM });

  // ===================== EFFECTS =====================

  /** Reset form when modal opens */
  useEffect(() => {
    if (!isOpen) return;

    setCurrentMode(initialMode);
    setErrors({});
    setFormError("");
    setIsSuccess(false);

    const initialForm =
      initialMode === "edit" && vendorData
        ? {
            // Populate form with existing vendor data
            vendorName: vendorData.vendorName || "",
            contactName: vendorData.contactName || "",
            contactPhone: formatPhoneDisplay(vendorData.contactPhone),
            contactEmail: vendorData.contactEmail || "",
            vendorCategory: Array.isArray(vendorData.vendorCategory)
              ? vendorData.vendorCategory
              : [],
            isClientFacing: vendorData.isClientFacing || false,
            hasShowroom: vendorData.hasShowroom || false,
            address: vendorData.address || "",
            website: vendorData.website || "",
            vendorType: Array.isArray(vendorData.vendorType)
              ? vendorData.vendorType
              : [],
            poolWorkVendor: vendorData.poolWorkVendor || false,
            notes: vendorData.notes || "",
            status: vendorData.status || "",
            logo: vendorData.logo?.[0] || null,
          }
        : { ...EMPTY_FORM };

    setForm(initialForm);
    baselineRef.current = initialForm;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // ===================== HANDLERS =====================

  /**
   * Update a single form field
   * @param {string} field - Field name
   * @param {*} value - New value
   */
  const handleChange = useCallback((field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear field error on change
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setFormError("");
  }, []);

  /**
   * Upload a single logo file to Cloudinary.
   * Shared by the click-to-browse and drag-and-drop paths.
   * @param {File} file - Image file to upload
   */
  const uploadLogoFile = async (file) => {
    if (!file) return;

    // Validate file type and size. MIME types must match what
    // cloudinaryService.validateFile allows (no SVG).
    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        logo: "Please select an image file (JPG, PNG, or WebP)",
      }));
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, logo: "File must be under 10MB" }));
      return;
    }

    setLogoUploading(true);
    setErrors((prev) => ({ ...prev, logo: undefined }));

    try {
      const result = await uploadFile(file, { folder: "vendor-logos" });
      handleChange("logo", { url: result.url, filename: file.name });
    } catch (error) {
      console.error("[VendorModal] Logo upload error:", error);
      setErrors((prev) => ({
        ...prev,
        logo: error?.message || "Upload failed. Please try again.",
      }));
    } finally {
      setLogoUploading(false);
    }
  };

  /**
   * Handle logo file selection from the hidden file input
   * @param {Event} e - File input change event
   */
  const handleLogoInputChange = (e) => {
    const file = e.target.files?.[0];
    // Reset input immediately so selecting the same file again re-fires change
    if (logoInputRef.current) logoInputRef.current.value = "";
    if (file) uploadLogoFile(file);
  };

  /**
   * Drag-and-drop handlers for the logo dropzone
   */
  const handleLogoDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!logoUploading) setIsLogoDragging(true);
  };

  const handleLogoDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLogoDragging(false);
  };

  const handleLogoDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLogoDragging(false);
    if (logoUploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) uploadLogoFile(file);
  };

  /**
   * Remove the current logo
   */
  const handleRemoveLogo = () => {
    handleChange("logo", null);
  };

  /**
   * Validate the form based on current mode
   * Required: Vendor Name, Vendor Category, at least one of Phone or Email.
   * Address is required when Has Showroom is checked.
   * @return {boolean} True if valid
   */
  const validate = () => {
    const newErrors = {};

    if (!form.vendorName.trim()) {
      newErrors.vendorName = "Vendor name is required";
    }

    if (!form.vendorCategory || form.vendorCategory.length === 0) {
      newErrors.vendorCategory = "At least one category is required";
    }

    if (!form.contactPhone.trim() && !form.contactEmail.trim()) {
      newErrors.contactPhone = "Phone or email is required";
      newErrors.contactEmail = "Phone or email is required";
    }

    if (form.hasShowroom && !form.address.trim()) {
      newErrors.address = "Address is required when Has Showroom is checked";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Submit the form — create or update
   */
  const handleSubmit = async () => {
    if (!validate()) return;

    setIsSaving(true);
    setFormError("");

    try {
      // Build the data payload
      const payload = { ...form };

      // Format logo for Airtable attachment format
      if (payload.logo && payload.logo.url) {
        payload.logo = [{ url: payload.logo.url }];
      } else {
        payload.logo = null;
      }

      if (currentMode === "edit" && recordId) {
        // Update existing vendor
        await mutations.updateVendor.mutateAsync({
          vendorId: recordId,
          updates: payload,
        });
      } else {
        // Create new vendor
        await mutations.createVendor.mutateAsync(payload);
      }

      setIsSuccess(true);

      // Auto-close after brief success display
      setTimeout(() => {
        setIsSuccess(false);
        onSave?.();
        onClose();
      }, 1200);
    } catch (error) {
      console.error("[VendorModal] Save error:", error);
      setFormError(
        error?.message || "Failed to save vendor. Please try again.",
      );
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Close the modal, warning first if there are unsaved changes.
   */
  const handleClose = () => {
    const isDirty =
      JSON.stringify(form) !== JSON.stringify(baselineRef.current);
    if (isDirty && !isSuccess) {
      if (
        !window.confirm(
          "You have unsaved changes. Are you sure you want to close?",
        )
      ) {
        return;
      }
    }
    onClose();
  };

  // ===================== RENDER GUARDS =====================

  if (!isOpen) return null;

  /** Whether to show "full" fields (full add or edit) */
  const showFullFields = currentMode === "full" || currentMode === "edit";
  const isEditMode = currentMode === "edit";
  const modalTitle = isEditMode
    ? "Edit Vendor"
    : currentMode === "full"
      ? "Add Vendor — Full Details"
      : "Quick Add Vendor";

  // ===================== RENDER =====================

  return (
    <div className="afm-overlay" onClick={handleClose}>
      <div
        className={`afm-modal ${showFullFields ? "afm-modal-wide" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="afm-header">
          <div className="afm-header-left">
            <div className="afm-header-icon">
              <FaHardHat />
            </div>
            <h2 className="afm-title">{modalTitle}</h2>
          </div>
          <button
            className="afm-close-btn"
            onClick={handleClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* Mode Toggle (only for create, not edit) */}
        {!isEditMode && (
          <div className="afm-mode-toggle">
            <button
              className={`afm-mode-btn ${
                currentMode === "quick" ? "afm-mode-active" : ""
              }`}
              onClick={() => setCurrentMode("quick")}
            >
              Quick Add
            </button>
            <button
              className={`afm-mode-btn ${
                currentMode === "full" ? "afm-mode-active" : ""
              }`}
              onClick={() => setCurrentMode("full")}
            >
              Full Details
            </button>
          </div>
        )}

        {/* Success State */}
        {isSuccess ? (
          <div className="afm-body">
            <div className="afm-success">
              <div className="afm-success-icon">
                <FaCheck />
              </div>
              <h3 className="afm-success-title">
                {isEditMode ? "Vendor Updated!" : "Vendor Created!"}
              </h3>
              <p className="afm-success-message">
                {isEditMode
                  ? `${form.vendorName} has been updated.`
                  : `${form.vendorName} has been added.`}
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* Form Body */}
            <div className="afm-body">
              {/* Form Error Banner */}
              {formError && (
                <div className="afm-form-error">
                  <FaExclamationTriangle />
                  <span>{formError}</span>
                </div>
              )}

              {/* === SECTION: Basic Info === */}
              <div className="afm-section">
                <div className="afm-section-title">Vendor Information</div>

                <div className="afm-field">
                  <label className="afm-label">
                    Vendor Name{" "}
                    <span className="afm-required">*</span>
                  </label>
                  <input
                    type="text"
                    className={`afm-input ${errors.vendorName ? "afm-input-error" : ""}`}
                    value={form.vendorName}
                    onChange={(e) => handleChange("vendorName", e.target.value)}
                    placeholder="Business name"
                    autoFocus
                  />
                  {errors.vendorName && (
                    <div className="afm-error">{errors.vendorName}</div>
                  )}
                </div>

                <div className="afm-field">
                  <label className="afm-label">
                    Vendor Category{" "}
                    <span className="afm-required">*</span>
                  </label>
                  <MultiSelectDropdown
                    options={VENDOR_CATEGORIES}
                    selected={form.vendorCategory}
                    onChange={(val) => handleChange("vendorCategory", val)}
                    placeholder="Select categories..."
                  />
                  {errors.vendorCategory && (
                    <div className="afm-error">{errors.vendorCategory}</div>
                  )}
                </div>
              </div>

              {/* === SECTION: Contact Info === */}
              <div className="afm-section">
                <div className="afm-section-title">Contact</div>

                <div className="afm-field">
                  <label className="afm-label">Contact Name</label>
                  <input
                    type="text"
                    className="afm-input"
                    value={form.contactName}
                    onChange={(e) =>
                      handleChange("contactName", e.target.value)
                    }
                    placeholder="Primary contact"
                  />
                </div>

                <div className="afm-row">
                  <div className="afm-field">
                    <label className="afm-label">
                      Phone{" "}
                      <span className="afm-required">*</span>
                    </label>
                    <input
                      type="tel"
                      className={`afm-input ${
                        errors.contactPhone ? "afm-input-error" : ""
                      }`}
                      value={form.contactPhone}
                      onChange={(e) =>
                        handleChange(
                          "contactPhone",
                          formatPhoneInput(e.target.value),
                        )
                      }
                      placeholder="(555) 555-5555"
                    />
                    {errors.contactPhone && (
                      <div className="afm-error">{errors.contactPhone}</div>
                    )}
                  </div>
                  <div className="afm-field">
                    <label className="afm-label">
                      Email{" "}
                      <span className="afm-required">*</span>
                    </label>
                    <input
                      type="email"
                      className={`afm-input ${
                        errors.contactEmail ? "afm-input-error" : ""
                      }`}
                      value={form.contactEmail}
                      onChange={(e) =>
                        handleChange("contactEmail", e.target.value)
                      }
                      placeholder="email@vendor.com"
                    />
                    {errors.contactEmail && (
                      <div className="afm-error">{errors.contactEmail}</div>
                    )}
                  </div>
                </div>
              </div>

              {/* === SECTION: Settings (checkboxes + conditional address) === */}
              <div className="afm-section">
                <div className="afm-section-title">Settings</div>

                <div className="afm-checkbox-group">
                  <label className="afm-checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.isClientFacing}
                      onChange={(e) =>
                        handleChange("isClientFacing", e.target.checked)
                      }
                    />
                    <span>Client-Facing</span>
                  </label>
                  <label className="afm-checkbox-label">
                    <input
                      type="checkbox"
                      checked={form.hasShowroom}
                      onChange={(e) =>
                        handleChange("hasShowroom", e.target.checked)
                      }
                    />
                    <span>Has Showroom</span>
                  </label>
                  {showFullFields && (
                    <label className="afm-checkbox-label">
                      <input
                        type="checkbox"
                        checked={form.poolWorkVendor}
                        onChange={(e) =>
                          handleChange("poolWorkVendor", e.target.checked)
                        }
                      />
                      <span>Pool Work Vendor</span>
                    </label>
                  )}
                </div>

                {/* Address — in Quick Add: only shows when Has Showroom checked.
                    In Full/Edit: always visible. Required when Has Showroom is checked. */}
                {(showFullFields || form.hasShowroom) && (
                  <div className="afm-field afm-field-spaced">
                    <label className="afm-label">
                      {form.hasShowroom ? "Showroom Address" : "Address"}
                      {form.hasShowroom ? (
                        <span className="afm-required"> *</span>
                      ) : (
                        <span className="afm-label-optional">(optional)</span>
                      )}
                    </label>
                    <textarea
                      className={`afm-textarea ${
                        errors.address ? "afm-input-error" : ""
                      }`}
                      value={form.address}
                      onChange={(e) => handleChange("address", e.target.value)}
                      placeholder={
                        form.hasShowroom
                          ? "Showroom / business address"
                          : "Business address"
                      }
                      rows={2}
                    />
                    {errors.address && (
                      <div className="afm-error">{errors.address}</div>
                    )}
                  </div>
                )}
              </div>

              {/* === FULL-ONLY SECTIONS === */}
              {showFullFields && (
                <>
                  {/* Website & Vendor Type */}
                  <div className="afm-section">
                    <div className="afm-section-title">Additional Details</div>

                    <div className="afm-field">
                      <label className="afm-label">Website</label>
                      <input
                        type="url"
                        className="afm-input"
                        value={form.website}
                        onChange={(e) =>
                          handleChange("website", e.target.value)
                        }
                        placeholder="https://vendor-website.com"
                      />
                    </div>

                    <div className="afm-field">
                      <label className="afm-label">Vendor Type</label>
                      <MultiSelectDropdown
                        options={VENDOR_TYPES}
                        selected={form.vendorType}
                        onChange={(val) => handleChange("vendorType", val)}
                        placeholder="Supplier, Installer, or both"
                      />
                    </div>
                  </div>

                  {/* Logo Upload */}
                  <div className="afm-section">
                    <label className="afm-label">Vendor Logo</label>

                    {/* Preview of current logo (shown once uploaded) */}
                    {form.logo?.url && !logoUploading && (
                      <div className="afm-logo-preview-section">
                        <img
                          src={form.logo.url}
                          alt="Vendor logo"
                          className="afm-logo-preview-img"
                        />
                        <div className="afm-logo-preview-info">
                          <span className="afm-logo-preview-name">
                            {form.logo.filename || "Vendor logo"}
                          </span>
                          <div className="afm-logo-preview-actions">
                            <button
                              type="button"
                              className="afm-logo-btn"
                              onClick={() => logoInputRef.current?.click()}
                            >
                              <FaSyncAlt />
                              Replace
                            </button>
                            <button
                              type="button"
                              className="afm-logo-btn afm-logo-btn-remove"
                              onClick={handleRemoveLogo}
                            >
                              <FaTrash />
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Dropzone (hidden once a logo is in place, unless uploading) */}
                    {(!form.logo?.url || logoUploading) && (
                      <div
                        className={`afm-dropzone ${
                          isLogoDragging ? "afm-dropzone-dragging" : ""
                        } ${logoUploading ? "afm-dropzone-disabled" : ""}`}
                        onDragOver={handleLogoDragOver}
                        onDragLeave={handleLogoDragLeave}
                        onDrop={handleLogoDrop}
                        onClick={() =>
                          !logoUploading && logoInputRef.current?.click()
                        }
                      >
                        {logoUploading ? (
                          <div className="afm-dropzone-content">
                            <FaSpinner className="afm-dropzone-icon spinning" />
                            <p className="afm-dropzone-text">Uploading…</p>
                          </div>
                        ) : (
                          <div className="afm-dropzone-content">
                            <FaCloudUploadAlt className="afm-dropzone-icon" />
                            <p className="afm-dropzone-text">
                              Drag &amp; drop a logo here or click to browse
                            </p>
                            <p className="afm-dropzone-hint">
                              JPG, PNG, or WebP • Max 10MB
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleLogoInputChange}
                      className="afm-hidden-input"
                    />

                    {errors.logo && (
                      <div className="afm-error">{errors.logo}</div>
                    )}
                  </div>
                  {/* Vendor Status */}
                  <div className="afm-field">
                    <label className="afm-label">Status</label>
                    <select
                      className="afm-input"
                      value={form.status}
                      onChange={(e) => handleChange("status", e.target.value)}
                    >
                      <option value="">Select status...</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>

                  {/* Notes */}
                  <div className="afm-section">
                    <div className="afm-field">
                      <label className="afm-label">
                        Vendor Notes
                        <span className="afm-label-optional">
                          (visible to clients if client-facing)
                        </span>
                      </label>
                      <textarea
                        className="afm-textarea"
                        value={form.notes}
                        onChange={(e) => handleChange("notes", e.target.value)}
                        placeholder="Notes about this vendor..."
                        rows={3}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="afm-footer">
              <button
                className="afm-btn afm-btn-secondary"
                onClick={handleClose}
                type="button"
              >
                Cancel
              </button>
              <button
                className="afm-btn afm-btn-primary"
                onClick={handleSubmit}
                disabled={isSaving || logoUploading}
                type="button"
              >
                {isSaving ? (
                  <>
                    <FaSpinner className="spinning" />
                    Saving...
                  </>
                ) : isEditMode ? (
                  <>
                    <FaCheckCircle /> Save Changes
                  </>
                ) : (
                  <>
                    <FaPlusCircle /> Create Vendor
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VendorModal;
